/* Penguin Find a Dealer service-area map. All data requests below are fixed public assets.
   This file sends nothing anywhere. The map is scenery that proves coverage: circles are
   drawn and highlighted but not clickable. The lead form (find-dealer-form.js) drives it
   through window.__penguinAreaMap, which is set once the map data has loaded:
     lookup(zip)    -> coverage facts for a ZIP, no side effects
     flyToZip(zip)  -> same facts, and the map zooms to the ZIP and marks it
     nearbyCount(zip), reset(), areaCount()
   A 'pdam:ready' event fires on document when the handle is set (or the map failed). */
import { geoAlbersUsa, geoConicEqualArea, geoPath, geoCentroid, geoContains, feature } from './penguin-area-map-vendor.js';
import { circle, distanceMiles, normalizeZip, RADIUS_MILES } from './penguin-area-map-core.js';

const statesById = {'01':['AL','Alabama'],'02':['AK','Alaska'],'04':['AZ','Arizona'],'05':['AR','Arkansas'],'06':['CA','California'],'08':['CO','Colorado'],'09':['CT','Connecticut'],'10':['DE','Delaware'],'11':['DC','District of Columbia'],'12':['FL','Florida'],'13':['GA','Georgia'],'15':['HI','Hawaii'],'16':['ID','Idaho'],'17':['IL','Illinois'],'18':['IN','Indiana'],'19':['IA','Iowa'],'20':['KS','Kansas'],'21':['KY','Kentucky'],'22':['LA','Louisiana'],'23':['ME','Maine'],'24':['MD','Maryland'],'25':['MA','Massachusetts'],'26':['MI','Michigan'],'27':['MN','Minnesota'],'28':['MS','Mississippi'],'29':['MO','Missouri'],'30':['MT','Montana'],'31':['NE','Nebraska'],'32':['NV','Nevada'],'33':['NH','New Hampshire'],'34':['NJ','New Jersey'],'35':['NM','New Mexico'],'36':['NY','New York'],'37':['NC','North Carolina'],'38':['ND','North Dakota'],'39':['OH','Ohio'],'40':['OK','Oklahoma'],'41':['OR','Oregon'],'42':['PA','Pennsylvania'],'44':['RI','Rhode Island'],'45':['SC','South Carolina'],'46':['SD','South Dakota'],'47':['TN','Tennessee'],'48':['TX','Texas'],'49':['UT','Utah'],'50':['VT','Vermont'],'51':['VA','Virginia'],'53':['WA','Washington'],'54':['WV','West Virginia'],'55':['WI','Wisconsin'],'56':['WY','Wyoming']};
const ns='http://www.w3.org/2000/svg';
const svgElement=(tag,attrs)=>{const el=document.createElementNS(ns,tag);for(const [key,value] of Object.entries(attrs))el.setAttribute(key,String(value));return el;};
const publish=api=>{window.__penguinAreaMap=api;document.dispatchEvent(new CustomEvent('pdam:ready'));};

async function initialize(root) {
  const $=selector=>root.querySelector(selector), $$=selector=>[...root.querySelectorAll(selector)];
  const svg=$('.pdam-map'), scene=$('.pdam-scene');
  let states=[],areas=[],zips={},projection,path,region='us',nearby=null,view={k:1,x:0,y:0};
  let drag=null,you=null,flight=0;
  const reduceMotion=()=>matchMedia('(prefers-reduced-motion: reduce)').matches;
  const setText=(selector,text)=>{const el=$(selector);if(el)el.textContent=text;};
  const captionDefault=$('.pdam-caption-text').textContent;

  // Exactly three static, public resource reads.
  try {
    const urls=['map','areas','zips'].map(key=>root.dataset['pdam'+key[0].toUpperCase()+key.slice(1)]);
    const [atlas,data,zipData]=await Promise.all(urls.map(async url=>{
      const r=await fetch(url,{credentials:'omit',referrerPolicy:'no-referrer'});
      if(!r.ok)throw new Error('Public map resource unavailable');return r.json();
    }));
    states=feature(atlas,atlas.objects.states).features.filter(s=>statesById[String(s.id).padStart(2,'0')]);
    zips=zipData;
    const ids=new Set();
    if(!Array.isArray(data.areas))throw new Error('Invalid area data');
    areas=data.areas.map(a=>{
      if(Object.keys(a).sort().join(',')!=='center,id' || !/^A\d{3,6}$/.test(a.id) || ids.has(a.id) || !Array.isArray(a.center) || a.center.length!==2 || a.center.some(n=>!Number.isFinite(n)) || Math.abs(a.center[0])>180 || a.center[1]<18 || a.center[1]>72)throw new Error('Invalid anonymous area');
      ids.add(a.id);
      const state=states.find(s=>geoContains(s,a.center));
      const geographicFallback=a.center[1]>50&&(a.center[0]<-130||a.center[0]>170)?'AK':a.center[0]<-154&&a.center[0]>-180&&a.center[1]>=18&&a.center[1]<30?'HI':'other';
      return {...a,state:state?statesById[String(state.id).padStart(2,'0')][0]:geographicFallback,geometry:circle(a.center)};
    });
    drawMap();
  } catch {
    setText('.pdam-caption-text','The map couldn’t load');
    publish({failed:true});
    return;
  }

  function drawMap(){
    const displayed=states.filter(s=>region==='us' || (region==='ak'?String(s.id)==='02':String(s.id)==='15'));
    const collection={type:'FeatureCollection',features:displayed};
    projection=region==='us'?geoAlbersUsa():region==='ak'?geoConicEqualArea().rotate([154,0]).center([0,63]).parallels([55,65]):geoConicEqualArea().rotate([157,0]).center([0,20.5]).parallels([8,18]);
    projection.fitExtent([[35,60],[865,510]],collection);path=geoPath(projection);
    $('.pdam-states').replaceChildren();$('.pdam-labels').replaceChildren();$('.pdam-circles').replaceChildren();
    for(const state of displayed){
      $('.pdam-states').append(svgElement('path',{d:path(state)||''}));
      const center=projection(geoCentroid(state));
      const info=statesById[String(state.id).padStart(2,'0')];
      // Tiny northeastern states' labels would collide, so they are omitted.
      if(center && !['RI','DE','DC','MA','NJ','CT'].includes(info[0])){
        const label=svgElement('text',{x:center[0],y:center[1]});label.textContent=region==='us'?info[0]:info[1];$('.pdam-labels').append(label);
      }
    }
    for(const area of areas){
      if(region!=='us'&&area.state!==(region==='ak'?'AK':'HI'))continue;
      const center=projection(area.center);if(!center)continue;
      const d=path(area.geometry);if(!d)continue;
      $('.pdam-circles').append(svgElement('path',{d,'data-pdam-id':area.id,class:nearby?.has(area.id)?'pdam-near':''}),svgElement('circle',{cx:center[0],cy:center[1],r:2}));
    }
    placeYou();
    resetView();
    setText('.pdam-inset-note',region==='us'?'Alaska and Hawaii are shown as insets.':region==='ak'?'Alaska detail.':'Hawaii detail.');
    svg.setAttribute('aria-label',(region==='us'?'United States':region==='ak'?'Alaska':'Hawaii')+' service-area map');
  }
  // The visitor's ZIP-area center, drawn above the dealer circles once they have checked a ZIP.
  function placeYou(){
    const marker=$('.pdam-you'),point=you&&projection(you);
    marker.toggleAttribute('hidden',!point);if(!point)return;
    for(const c of marker.querySelectorAll('circle')){c.setAttribute('cx',point[0]);c.setAttribute('cy',point[1]);}
  }
  function setRegion(next){region=next;drawMap();}
  function drawView(){
    view.x=Math.min(900*(view.k-1)*.5,Math.max(-900*(view.k-1)*1.5,view.x));
    view.y=Math.min(580*(view.k-1)*.5,Math.max(-580*(view.k-1)*1.5,view.y));
    scene.setAttribute('transform',`translate(${view.x} ${view.y}) scale(${view.k})`);
    $('.pdam-labels').style.opacity=view.k>5?'0':'1';
    const bounds=svg.getBoundingClientRect(),scale=Math.min(bounds.width/900,bounds.height/580)||1;
    $$('.pdam-labels text').forEach(n=>n.style.fontSize=`${(bounds.width<500?8:11)/scale/view.k}px`);
    // Keep the "you" marker a constant on-screen size at every zoom.
    $('.pdam-you-dot').setAttribute('r',String(7/scale/view.k));$('.pdam-you-ring').setAttribute('r',String(16/scale/view.k));
    $('[data-pdam-zoom="out"]').disabled=view.k===1;$('[data-pdam-zoom="in"]').disabled=view.k>=8;
  }
  function resetView(){cancelAnimationFrame(flight);view={k:1,x:0,y:0};drawView();}
  function zoom(factor){cancelAnimationFrame(flight);const next=Math.max(1,Math.min(8,view.k*factor));const ratio=next/view.k;view={k:next,x:450-(450-view.x)*ratio,y:290-(290-view.y)*ratio};drawView();}
  // Eased flight from the current view to a target, so a ZIP lookup visibly zooms in.
  function flyTo(target){
    cancelAnimationFrame(flight);
    if(reduceMotion()){view=target;drawView();return;}
    const from={...view},start=performance.now(),ms=1100;
    const frame=now=>{
      const t=Math.min(1,(now-start)/ms),e=t<.5?4*t*t*t:1-Math.pow(-2*t+2,3)/2;
      view={k:from.k+(target.k-from.k)*e,x:from.x+(target.x-from.x)*e,y:from.y+(target.y-from.y)*e};drawView();
      if(t<1)flight=requestAnimationFrame(frame);
    };
    flight=requestAnimationFrame(frame);
  }
  function svgPoint(e){return new DOMPoint(e.clientX,e.clientY).matrixTransform(svg.getScreenCTM().inverse());}

  // Coverage facts for a ZIP, computed from the published anonymous areas. No side effects.
  function lookup(value){
    const zip=normalizeZip(value);
    if(!zip)return {status:'invalid'};
    const center=zips[zip];
    if(!center)return {zip,status:'unmapped'};
    if(!geoAlbersUsa()(center)&&!(center[1]>50&&(center[0]<-130||center[0]>170)))return {zip,center,status:'outside'};
    const ranked=areas.map(a=>({id:a.id,center:a.center,miles:distanceMiles(center,a.center)})).sort((a,b)=>a.miles-b.miles);
    const near=ranked.filter(a=>a.miles<=RADIUS_MILES);
    return {zip,center,status:near.length?'covered':'none',count:near.length,radiusMiles:RADIUS_MILES,
      nearestMiles:ranked.length?Math.round(ranked[0].miles):null,nearIds:near.map(a=>a.id),nearest:ranked[0]||null,
      region:center[1]>50?'ak':center[0]<-150?'hi':'us'};
  }
  // The same facts, plus the visual: nearby areas highlighted, the ZIP marked, the map flown in.
  function flyToZip(value){
    const result=lookup(value);
    if(result.status!=='covered'&&result.status!=='none')return result;
    nearby=new Set(result.nearIds);you=result.center;
    setRegion(result.region);
    const p=projection(result.center);
    if(p){
      // Close enough that the 30-mile circles read as coverage, but wide enough to show the nearest dealer.
      const q=result.nearest&&projection(result.nearest.center),gap=q?Math.hypot(q[0]-p[0],q[1]-p[1]):0;
      const k=Math.max(1.4,Math.min(4.5,gap?170/gap:4.5));
      flyTo({k,x:450-p[0]*k,y:290-p[1]*k});
    }
    root.classList.add('pdam-has-result');
    setText('.pdam-caption-text',result.status==='covered'?`${result.zip} · ${result.count} dealer ${result.count===1?'area':'areas'} within 30 mi`:`${result.zip} · nearest dealer ${result.nearestMiles} mi away`);
    return result;
  }
  function reset(){nearby=null;you=null;root.classList.remove('pdam-has-result');setText('.pdam-caption-text',captionDefault);setRegion('us');}

  $('[data-pdam-zoom="in"]').addEventListener('click',()=>zoom(1.5));$('[data-pdam-zoom="out"]').addEventListener('click',()=>zoom(1/1.5));$('[data-pdam-reset-view]').addEventListener('click',resetView);
  svg.addEventListener('keydown',e=>{
    if(e.target!==svg)return;
    if(['+','=','-','ArrowLeft','ArrowRight','ArrowUp','ArrowDown','Home'].includes(e.key))e.preventDefault();
    if(e.key==='+'||e.key==='=')zoom(1.5);if(e.key==='-')zoom(1/1.5);if(e.key==='Home')resetView();
    const moves={ArrowLeft:[50,0],ArrowRight:[-50,0],ArrowUp:[0,50],ArrowDown:[0,-50]};
    if(moves[e.key]){if(view.k===1)zoom(1.5);view.x+=moves[e.key][0];view.y+=moves[e.key][1];drawView();}
  });
  new ResizeObserver(()=>drawView()).observe(svg);
  // Mouse drag pans. Touch is left to the page (touch-action: pan-y) so a phone scrolls past the map.
  svg.addEventListener('pointerdown',e=>{if(e.pointerType==='touch'||e.button!==0)return;cancelAnimationFrame(flight);const p=svgPoint(e);drag={id:e.pointerId,x:p.x,y:p.y,originalX:view.x,originalY:view.y};});
  svg.addEventListener('pointermove',e=>{if(!drag||drag.id!==e.pointerId)return;const p=svgPoint(e);const dx=p.x-drag.x,dy=p.y-drag.y;if(Math.hypot(dx,dy)>5){svg.setPointerCapture(e.pointerId);view.x=drag.originalX+dx;view.y=drag.originalY+dy;drawView();}});
  const endDrag=()=>{drag=null;};svg.addEventListener('pointerup',endDrag);svg.addEventListener('pointercancel',endDrag);svg.addEventListener('lostpointercapture',endDrag);

  publish({lookup,flyToZip,nearbyCount:value=>lookup(value).count??0,reset,areaCount:()=>areas.length});
}
for(const root of document.querySelectorAll('.pdam-root'))initialize(root);
